import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ColDef } from '@ag-grid-community/core'
import { StatisticalAnalysisDatasource } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-datasource'
import {
    StatisticalAnalysisGeneralStatisticsDossierBaseRequest, StatisticalAnalysisGeneralStatisticsDossierDetailRequest,
    StatisticalAnalysisGeneralStatisticsDossierDetailViewTable, StatisticalAnalysisGeneralStatisticsDossierDetailViewTableContentCase,
    StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum, StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum,
    StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum,
    StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum
} from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.dto'
import { generalStatisticsDossierService } from '@/modules/statistical-analysis-module/services/general-statistics-dossier/general-statistics-dossier.service'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { Nullable } from '@/shared/types/app.types'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisCaseTypes } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisTemporalFilterEnum } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { StatisticalAnalysisGeneralDossierExternalInfo } from '@/modules/statistical-analysis-module/views/general-statistics-dossier/general-statistics-dossier.model'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { concat, forEach } from 'lodash'
import { ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { getIconHeaders } from '@/modules/statistical-analysis-module/shared/helpers/helpers'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'

export class StatisticalAnalysisHandlerDatasourceGeneralDossier extends StatisticalAnalysisDatasource<StatisticalAnalysisGeneralStatisticsDossierDetailViewTableContentCase> {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>) {
        super()
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
    }

    public getGridColumns(): ColDef[] | ColGroupDef[] | ExportedColGroupDef[] | ExportedColDef [] {
        return concat([], [{ type: AgGridColumnType.CUSTOM_CHECKBOX } as ColDef], this._filterGroupedGridColumns(), this._getStatColumns())
    }

    public async paginator(filterModel: CommonFilterModel, params: CommonParams, scrollId: string): Promise<InfinitePaginatorResponse<StatisticalAnalysisGeneralStatisticsDossierDetailViewTableContentCase>> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisGeneralDossierExternalInfo
        const baseRequest: StatisticalAnalysisGeneralStatisticsDossierBaseRequest = generalStatisticsDossierService.getBaseRequest(sidebarFilter)
        const detailRequest: StatisticalAnalysisGeneralStatisticsDossierDetailRequest = {
            groupByFields: (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName),
            infoDetail: this.statisticalAnalysisFilters?.temporalFilter as StatisticalAnalysisTemporalFilterEnum
        }
        const typeCase = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        const state = sidebarFilter.trackingGeneralSelected?.key as StatisticalAnalysisCaseTypes
        const detailState = sidebarFilter.trackingDetailSelected?.key as StatisticalAnalysisCaseTypes
        const response: StatisticalAnalysisGeneralStatisticsDossierDetailViewTable = await generalStatisticsDossierService.getDetailViewTable(filterModel, params, typeCase, state, detailState, baseRequest, detailRequest, scrollId)

        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response.scrollPage.scrollId)),
                size: response.scrollPage.size,
                totalElements: null
            }
        }
    }

    private _getStatsCaseColum(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.cases'),
            field: 'stats.totalCases',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false
        }]
    }

    private _getStatsSampleColum(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.samples'),
            field: 'stats.totalSamples',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false,
            columnGroupShow: 'false'
        }]
    }

    private _getStatsPortionsColum(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.portions'),
            field: 'stats.totalPortions',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false,
            columnGroupShow: 'false'
        }]
    }

    private _getStatsSubPortionsColum(): ExportedColDef[] {
        return [{
            type: AgGridColumnType.NUMBER,
            headerName: i18n.tc('statisticalAnalysis.grids.subportions'),
            field: 'stats.totalSubportions',
            exportType: AgGridExportType.NUMBER,
            minWidth: 100,
            flex: 1,
            suppressSizeToFit: true,
            filter: false,
            sortable: false,
            columnGroupShow: 'false'
        }]
    }

    private _getStatFixColumns(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.percentage'),
                field: 'stats.percent',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.maximumTime'),
                field: 'stats.maxTime',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.minimumTime'),
                field: 'stats.minTime',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.average'),
                field: 'stats.mean',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.median'),
                field: 'stats.median',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.DECIMAL,
                headerName: i18n.tc('statisticalAnalysis.grids.standardDeviation'),
                field: 'stats.deviation',
                exportType: AgGridExportType.DOUBLE,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            }
        ]
    }

    private _getStatColumns(): ExportedColGroupDef[] {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisGeneralDossierExternalInfo
        const typeCase = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases
        let columns: ExportedColDef[]
        switch (typeCase) {
        case StatiscalAnalysisTypeCases.CASE:
            columns = concat([], this._getStatsCaseColum(), this._getStatsSampleColum(), this._getStatFixColumns())
            break
        case StatiscalAnalysisTypeCases.SAMPLE:
            columns = concat([], this._getStatsCaseColum(), this._getStatsSampleColum(), this._getStatsPortionsColum(), this._getStatFixColumns())
            break
        case StatiscalAnalysisTypeCases.PORTION:
            columns = concat([], this._getStatsCaseColum(), this._getStatsSampleColum(), this._getStatsPortionsColum(), this._getStatsSubPortionsColum(), this._getStatFixColumns())
            break
        case StatiscalAnalysisTypeCases.SUBPORTION:
            columns = concat([], this._getStatsCaseColum(), this._getStatsSampleColum(), this._getStatsPortionsColum(), this._getStatsSubPortionsColum(), this._getStatFixColumns())
            break
        default:
            columns = this._getStatFixColumns()
            break
        }
        return [
            {
                headerName: i18n.tc('statisticalAnalysis.grids.statistics'),
                children: columns
            }
        ]
    }

    private _getGroupingColumnsCases(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                field: 'caseType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 120,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.CASE_TYPE
                }
            },
            // TODO Pending to add
            // {
            //     type: AgGridColumnType.TEXT,
            //     headerName: i18n.tc('statisticalAnalysis.grids.destination'),
            //     field: 'destination.literal',
            //     exportField: 'destination.literal',
            //     exportType: AgGridExportType.TEXT,
            //     minWidth: 200,
            //     flex: 1,
            //     suppressSizeToFit: true,
            //     filter: false,
            //     sortable: false
            // },
            {
                type: AgGridColumnType.TEXT,
                field: 'destinationDepartment.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.DESTINATION_DEPARTMENT
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'destinationCenter.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.DESTINATION_CENTER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'destinationHealthManagementArea.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.DESTINATION_HEALTH_MANAGEMENT_AREA
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'laboratory.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.LABORATORY
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'pathologist.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.PATHOLOGIST
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'physicianCenter.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_CENTER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'physicianDepartment.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_DEPARTMENT
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'physicianHealthManagementArea.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_HEALTH_MANAGEMENT_AREA
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'resident.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.RESIDENT
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'physicianPetitioner.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.PHYSICIAN_PETITIONER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'priority.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 120,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.PRIORITY
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'regime.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.REGIME
                }
            },
            {
                type: AgGridColumnType.CHECK_ICON,
                field: 'caseOfReference.literal',
                exportType: AgGridExportType.BOOLEAN,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsCaseEnum.CASE_OF_REFERENCE
                }
            }
        ]
    }

    private _getGroupingColumnsSamples(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                field: 'sampleType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.SAMPLE_TYPE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'topographicCode.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.TOPOGRAPHIC_CODE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'procedure.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.PROCEDURE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'mostLikelyMorphologicCode.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.MOST_LIKELY_MORPHOLOGIC_CODE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'virtualShelf.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.VIRTUAL_SHELF
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'specialist.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.SPECIALIST
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'grossingOrProcessingResponsible.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.GROSSING_PROCESSING_RESPONSIBLE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'grossingTechController.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.GROSSING_TECH_CONTROLLER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'grossingCoordinator.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.GROSSING_COORDINATOR
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'grossingTutor.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSampleEnum.GROSSING_TUTOR
                }
            }
        ]
    }

    private _getGroupingColumnsPortions(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                field: 'portionType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum.PORTION_TYPE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'portionState.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum.PORTION_STATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'protocol.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum.PROTOCOL
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'registerLightDate.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum.REGISTER_LIGHT_DATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'processingEndLightDate.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum.PROCESSING_END_LIGHT_DATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'generationLightDate.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum.GENERATION_LIGHT_DATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'generationPersonnel.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsPortionEnum.GENERATION_PERSONNEL
                }
            }
        ]
    }

    private _getGroupingColumnsSubportions(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                field: 'subportionType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.SUBPORTION_TYPE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'assayGroup.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.ASSAY_GROUP
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'subportionState.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.SUBPORTION_STATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'assay.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.ASSAY
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'subportionProtocol.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.SUBPORTION_PROTOCOL
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'requestResponsible.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.REQUESTER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'requestingLightDate.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.REQUEST_LIGHT_DATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'cuttingLightDate.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.CUT_LIGHT_DATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'stainEndLightDate.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.STAIN_END_LIGHT_DATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'scanEndLightDate.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.SCAN_END_LIGHT_DATE
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'cuttingResponsible.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.CUTTER
                }
            },
            {
                type: AgGridColumnType.TEXT,
                field: 'subportionPriority.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false,
                headerComponentParams: {
                    columnFieldName: StatisticalAnalysisGeneralStatisticsDossierGroupingColumnsSubportionEnum.SUBPORTION_PRIORITY
                }
            }
        ]
    }

    private _getGroupingColumns(): ExportedColDef[] {
        return concat(
            [],
            this._getGroupingColumnsCases(),
            this._getGroupingColumnsSamples(),
            this._getGroupingColumnsPortions(),
            this._getGroupingColumnsSubportions()
        )
    }

    // TODO Pending to add into class StatisticalAnalysisDatasource
    private _extendColumnWithIconHeader(column: ExportedColDef, entityType: StatiscalAnalysisTypeCases): ExportedColDef {
        return {
            ...column,
            headerComponentParams: {
                ...column.headerComponentParams,
                template: `<span class="ag-header-cell-text"><i class="far ${getIconHeaders(entityType)} mr-2"></i>${column.headerName}</span>`
            }
        }
    }

    // TODO Pending to add into class StatisticalAnalysisDatasource
    private _filterGroupedGridColumns(): ColGroupDef[] {
        const colDefs: ColDef[] = []
        const groupedColumns = this._getGroupingColumns()
        forEach(this.statisticalAnalysisFilters?.grouping, (item: StatisticalAnalysisGroupingResponseField) => {
            const column = groupedColumns.find((groupColumn: ExportedColDef) => groupColumn.headerComponentParams.columnFieldName === item.fieldName) as ExportedColDef
            if (column) {
                column.headerName = item.translatedFieldName
                colDefs.push(this._extendColumnWithIconHeader(column, item.entity))
            }
        })
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.groupings'),
            children: colDefs
        }]
    }
}
